import axios from 'axios';
import { supabase } from './supabase';
import { BACKEND_URL } from './config';
import { SUPABASE_URL } from '../auth/constants';

function requestError(err) {
    const payload = err.response?.data || {};
    const message = payload.error || payload.message || err.message || 'Request failed';
    const error = new Error(typeof message === 'string' ? message : 'Request failed');
    const codeFromError = typeof payload.error === 'string' && /^[A-Z][A-Z0-9_]+$/.test(payload.error)
      ? payload.error
      : null;
    error.code = payload.code || codeFromError || null;
    error.status = err.response?.status || null;
    if (err.code === 'ECONNABORTED') {
        return new Error('That took too long. Try again.');
    }
    return error;
}

function tokenFromStorage() {
    try {
        const projectRef = SUPABASE_URL.split('//')[1].split('.')[0];
        const keys = [`sb-${projectRef}-auth-token`, 'sb:session'];
        for (const key of keys) {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const token = parsed?.access_token || parsed?.currentSession?.access_token;
            if (token) return token;
        }
    } catch (_err) {
        return null;
    }
    return null;
}

function withTimeout(promise, ms, message) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error(message)), ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

async function getAccessToken() {
    const stored = tokenFromStorage();
    if (stored) return stored;

    try {
        const { data: { session } } = await withTimeout(
            supabase.auth.getSession(),
            3000,
            'session-timeout'
        );
        if (session?.access_token) return session.access_token;
    } catch (_err) {
        // Fall through
    }

    throw new Error('Please log in again');
}

async function request(method, endpoint, data) {
    const token = await getAccessToken();

    try {
        const response = await axios({
            method,
            url: `${BACKEND_URL}${endpoint}`,
            data,
            headers: { Authorization: `Bearer ${token}` },
            timeout: 20000,
        });

        if (response.data?.error) {
            throw new Error(response.data.error);
        }
        return response.data;
    } catch (err) {
        if (err instanceof Error && err.message && !err.response && err.code !== 'ECONNABORTED') {
            throw err;
        }
        throw requestError(err);
    }
}

export async function fetchUserProfile() {
    return request('get', '/auth/me');
}

export async function updateUserProfile(fields) {
    return request('patch', '/api/user/profile', fields);
}

export async function clearSearchHistory() {
    return request('delete', '/api/user/search-history');
}

export async function patchConsent(consent) {
    return request('patch', '/api/user/consent', consent);
}

export async function exportUserData() {
    return request('get', '/api/user/export-data');
}

export async function deleteAccount() {
    try {
        return await request('delete', '/api/user/delete-data');
    } catch (err) {
        if (err.code === 'AUTH_ADMIN_UNAVAILABLE' || err.status === 503) {
            const next = new Error('Account delete is unavailable right now. Email livenow.enterprise@gmail.com.');
            next.code = 'AUTH_ADMIN_UNAVAILABLE';
            throw next;
        }
        if (err.code === 'AUTH_USER_DELETE_FAILED') {
            const next = new Error('Your LiveNow data was removed but sign-in may still exist. Email livenow.enterprise@gmail.com.');
            next.code = 'AUTH_USER_DELETE_FAILED';
            throw next;
        }
        throw err;
    }
}
